/**
 * 微信工具函数
 *
 * 提供微信环境检测、JSSDK 配置获取等纯函数工具。
 * 不依赖任何 React API，可在任意环境使用。
 */

// ==================== 类型定义 ====================

/**
 * 微信 JSSDK 配置（后端 /api/wechat/js-sdk/config 返回）
 */
export interface WeChatJsSdkConfig {
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
}

/**
 * 微信分享数据
 */
export interface WeChatShareData {
  /** 分享标题 */
  title: string;
  /** 分享描述（分享给朋友时显示，朋友圈不显示） */
  desc?: string;
  /** 分享链接，默认为当前页面 URL */
  link?: string;
  /** 分享图标 URL */
  imgUrl?: string;
}

/**
 * 后端统一响应格式
 */
interface ApiSuccessResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// ==================== 环境检测 ====================

/**
 * 检测是否为微信浏览器环境
 */
export function isWeChatBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.userAgent.toLowerCase().includes('micromessenger');
}

/**
 * 检测是否为 iOS 设备
 */
export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return !!navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
}

// ==================== URL 管理 ====================

const ENTRY_URL_KEY = 'wechat_entry_url';

/**
 * 获取用于 JSSDK 签名的 URL
 *
 * iOS 微信浏览器中，SPA 的 URL 不会随 history.pushState 更新，
 * 必须使用首次进入页面时的 URL 进行签名，否则签名会失败。
 * Android 微信和其他浏览器无此问题。
 */
export function getSignatureUrl(): string {
  if (isIOSDevice() && isWeChatBrowser()) {
    const entryUrl = sessionStorage.getItem(ENTRY_URL_KEY);
    if (entryUrl) {
      return entryUrl;
    }
  }
  // 默认使用当前 URL（移除 hash 部分）
  return window.location.href.split('#')[0];
}

/**
 * 记录 iOS 微信入口 URL
 *
 * 应在应用启动时尽早调用（如 main.tsx 或 App 初始化），
 * 确保记录的是用户首次进入微信 WebView 时的原始 URL。
 */
export function recordIOSEntryUrl(): void {
  if (isIOSDevice() && isWeChatBrowser()) {
    if (!sessionStorage.getItem(ENTRY_URL_KEY)) {
      const url = window.location.href.split('#')[0];
      sessionStorage.setItem(ENTRY_URL_KEY, url);
    }
  }
}

/**
 * 清除记录的入口 URL
 *
 * 在需要重新签名时调用（如用户主动刷新页面后重新进入）。
 */
export function clearStoredEntryUrl(): void {
  sessionStorage.removeItem(ENTRY_URL_KEY);
}

// ==================== API 调用 ====================

/**
 * 从后端获取 JSSDK 配置
 *
 * 调用后端 GET /api/wechat/js-sdk/config?url=<currentUrl>
 * 接口为公开（匿名落地页、分享页也可拉取签名）。
 *
 * @param url 当前页面 URL（用于生成签名）
 * @returns JSSDK 配置（appId, timestamp, nonceStr, signature, jsApiList）
 */
export async function fetchJsSdkConfig(url: string): Promise<WeChatJsSdkConfig> {
  const response = await fetch(
    `/api/wechat/js-sdk/config?url=${encodeURIComponent(url)}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    throw new Error(`获取 JSSDK 配置失败 (${response.status})`);
  }

  const result: ApiSuccessResponse<WeChatJsSdkConfig> = await response.json();

  if (!result.data) {
    throw new Error('JSSDK 配置为空');
  }

  return result.data;
}

/**
 * 微信开放平台 — 网站应用 JS-SDK 签名（`wx.config` 的 appId 为网站应用 AppID）
 *
 * 对应后端 `GET /api/wechat/open/js-sdk/config?url=`
 */
export async function fetchOpenPlatformJsSdkConfig(url: string): Promise<WeChatJsSdkConfig> {
  const response = await fetch(
    `/api/wechat/open/js-sdk/config?url=${encodeURIComponent(url)}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    throw new Error(`获取开放平台 JSSDK 配置失败 (${response.status})`);
  }

  const result: ApiSuccessResponse<WeChatJsSdkConfig> = await response.json();

  if (!result.data) {
    throw new Error('开放平台 JSSDK 配置为空');
  }

  return result.data;
}
